import { Button, DatePicker, Input, Modal, Space, Table } from "antd";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DebounceSelect from "../../components/DebounceSelect";
import { partnerTrackingSelector } from "../../feature/partner-tracking/partnerTrackingSelector";
import {
  getPartnerTrackingRequest,
  removePartnerTrackingsRequest,
} from "../../feature/partner-tracking/partnerTrackingSlice";
import { searchPartner } from "../../feature/referral-partners/referralPartnerApi";
import { appConfig } from "../../utils/appConfig";
import { searchUser } from "../../feature/tracking-links/trackingLinkApi";
import AddTrackingLink from "../TrackingLinks/AddTrackingLink";
import AddTracking from "./AddTracking";

const PartnerTracking = () => {
  const [partnerFilter, setPartnerFilter] = useState(null);
  // const [userFilter, setUserFilter] = useState(null);
  const [startDate, setStartDate] = useState(moment("2022-01-01"));
  const [endDate, setEndDate] = useState(moment(new Date()).add(1, "days"));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(appConfig.PAGE_SIZE);
  const [selectedPartnerTracking, setSelectedPartnerTracking] = useState();
  const [displayRemoveModal, setDisplayRemoveModal] = useState(false);
  const [displayAddTrackingModal, setDisplayAddTrackingModal] = useState(false);
  const [searchType, setSearchType] = useState("partner");
  const [userSearchContent, setUserSearchContent] = useState("");
  const {
    listPartnerTracking,
    total,
    isGetPartnerTracking,
    isCreatePartnerTracking,
    isRemovePartnerTracking,
  } = useSelector(({ partnerTrackingState }) =>
    partnerTrackingSelector(partnerTrackingState)
  );
  const dispatch = useDispatch();
  const totalRef = useRef(total);

  const columns = useMemo(() => {
    const result = [
      {
        title: "User Name",
        dataIndex: "metadata",
        key: "username",
        render: (value) =>
          `${value?.userFirstName || ""} ${value?.userLastName || ""}`,
      },
      {
        title: "User Email",
        dataIndex: "userEmail",
        key: "userEmail",
      },
      {
        title: "Referral Partner",
        dataIndex: "partner",
        key: "partner",
        render: (value) =>
          value.length > 0
            ? `${value[0]?.firstName || ""} ${value[0]?.lastName || ""}`
            : `${value?.firstName || ""} ${value?.lastName || ""}`,
      },
      {
        title: "DateTime User Clicks Partner's Tracking Link",
        dataIndex: "metadata",
        key: "userLastHitAt",
        render: (value) =>
          value?.userLastHitAt
            ? moment(value?.userLastHitAt).format("DD MMM YYYY h:mm a")
            : "N/A",
      },
      {
        title: "DateTime Account Created",
        dataIndex: "metadata",
        key: "userCreatedAt",
        render: (value) =>
          value?.userCreatedAt
            ? moment(value?.userCreatedAt).format("DD MMM YYYY h:mm a")
            : "N/A",
      },
      {
        title: "Action",
        key: "action",
        render: (_, item) => (
          <Space size="middle">
            <span
              className="pointer ant-typography ant-typography-danger"
              onClick={() => onRemovePartnerTracking(item)}
            >
              Delete
            </span>
          </Space>
        ),
      },
    ];
    return result;
  }, []);

  const fetchPartnerList = async (name) => {
    return searchPartner(name).then((res) => {
      const listPartner = res.data.map((partner) => ({
        label: `${partner.firstName} ${partner.lastName}`,
        value: partner._id,
      }));
      listPartner.unshift({
        label: "--/--",
        value: null,
      });
      return listPartner;
    });
  };

  const onRemovePartnerTracking = (data) => {
    setSelectedPartnerTracking(data);
    setDisplayRemoveModal(true);
  };

  const onConfirmRemove = () => {
    selectedPartnerTracking &&
      dispatch(removePartnerTrackingsRequest(selectedPartnerTracking));
    setDisplayRemoveModal(false);
  };

  const loadData = (newPage, sizeChanger, type, value) => {
    const limit = sizeChanger || pageSize;
    const pi = partnerFilter?.value || "";
    const st = moment(startDate).endOf("day").toISOString();
    const et = moment(endDate).endOf("day").toISOString();
    setPage(newPage);
    setPageSize(sizeChanger);
    setSearchType(type);
    setUserSearchContent(value);
    type === "user"
      ? dispatch(
          getPartnerTrackingRequest({
            ue: value || "",
            limit,
            offset: (newPage - 1) * limit,
            type,
          })
        )
      : dispatch(
          getPartnerTrackingRequest({
            pi,
            st,
            et,
            limit,
            offset: (newPage - 1) * limit,
            type: "partner",
          })
        );
  };

  const onFilterPartner = () => {
    loadData(1, pageSize, "partner");
  };

  const onSearchUser = (value) => {
    loadData(1, pageSize, "user", value);
  };

  const onChangeTable = (page, sizeChanger) => {
    loadData(page, sizeChanger, searchType, userSearchContent);
  };

  const onAddTracking = () => {
    setDisplayAddTrackingModal(true);
  };

  const onCancelModal = () => {
    setDisplayAddTrackingModal(false);
  };

  useEffect(() => {
    if (
      total < totalRef.current &&
      page !== total / pageSize &&
      page > 1 &&
      total > 0 &&
      total % pageSize === 0
    ) {
      loadData(page - 1, pageSize, searchType, userSearchContent);
      totalRef.current = total;
      return;
    }
    if (total > totalRef.current) {
      loadData(1, pageSize, searchType, userSearchContent);
      totalRef.current = total;
      return;
    }
    loadData(page, pageSize, searchType, userSearchContent);
    totalRef.current = total;
    // eslint-disable-next-line
  }, [total]);

  return (
    <div>
      <div className="title-page">Partner Tracking</div>
      <div className="filter-container mt-25 d-flex space-between">
        <div className="d-flex mb-5">
          <div className="date-choose mr-5">
            <div>Start Date Click</div>
            <DatePicker
              placeholder="DD MMM YYYY"
              format="DD MMM YYYY"
              onChange={(date) => setStartDate(date)}
              value={startDate}
            />
          </div>
          <div className="date-choose mr-25">
            <div>End Date Click</div>
            <DatePicker
              placeholder="DD MMM YYYY"
              format="DD MMM YYYY"
              onChange={(date) => setEndDate(date)}
              value={endDate}
            />
          </div>
          <div>
            <div>Filter by Referral Partner</div>
            <DebounceSelect
              className="w-200 mr-25"
              placeholder="Filter by Referral Partner"
              fetchOptions={fetchPartnerList}
              value={partnerFilter}
              onChange={(value) => {
                setPartnerFilter(value);
              }}
            />
          </div>
          <div>
            <div className="hidden">filter</div>
            <Button type="primary" onClick={onFilterPartner}>
              Filter
            </Button>
          </div>
        </div>
        <div className="d-flex">
          <div>
            <div>Search User by name or email</div>
            <Input.Search
              className="w-300"
              placeholder="Type name or email"
              onSearch={(value) => onSearchUser(value)}
              enterButton
              allowClear
            />
          </div>
        </div>
      </div>
      <div className="mt-5 text-right">
        <Button type="primary" onClick={onAddTracking}>
          Add Tracking
        </Button>
      </div>
      <Table
        className="mt-25"
        columns={columns}
        dataSource={listPartnerTracking}
        bordered
        pagination={{
          showTotal: (total) => `Total ${total} results`,
          defaultPageSize: appConfig.PAGE_SIZE,
          pageSize,
          defaultCurrent: 1,
          current: page,
          showSizeChanger: true,
          pageSizeOptions: [appConfig.PAGE_SIZE, 25, 50].sort((a, b) => a - b),
          total,
          onChange: (page, sizeChanger) => onChangeTable(page, sizeChanger),
        }}
        loading={
          isGetPartnerTracking ||
          isRemovePartnerTracking ||
          isCreatePartnerTracking
        }
        rowKey="_id"
      />
      {displayRemoveModal && (
        <Modal
          title="Are you sure?"
          visible={true}
          width={"50%"}
          className="modal-w-600"
          onOk={onConfirmRemove}
          onCancel={() => setDisplayRemoveModal(false)}
        >
          <p>
            Do you really want to delete this partner tracking? This process
            cannot be undone.
          </p>
        </Modal>
      )}
      {displayAddTrackingModal && <AddTracking onCancel={onCancelModal} />}
    </div>
  );
};

export default PartnerTracking;
